import { useEffect, useState } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import BannerCalcoloRanking from '../../../../components/report/listaDriverPage/ranking/BannerCalcoloRanking';
import ContainerCalcoloRanking from '../../../../components/report/listaDriverPage/ranking/ContainerCalcoloRanking';
import ReportButton from '../../../../components/report/listaDriverPage/ranking/ReportButton';
import ReportEditorModal from '../../../../components/report/listaDriverPage/ranking/ReportEditorModal';
import {
  useDriverScores,
  useFinalRanking,
  useReportFilesLasted,
  useScenarioScores,
  useScenarioWeights
} from '../../../../api/hooks/useRanking';
import { createReportFile, fetchExportXlsxById, fetchReopenById, fetchCompleteById, fetchReportById } from '../../../../api/functionApi';
import { useReport } from '../../../../api/hooks/useReport';
import { routesMap } from '../../../../routes/AppRoutes';

const RankingReportPage = () => {
  const { name } = useParams<{ name: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const id = (location.state as { id?: string })?.id;

  const { report } = useReport(id);
  const { driverScores, loading: loadingReport, error: errorReport } = useDriverScores(id);
  const { scenarioScores } = useScenarioScores(id);
  const { finalRanking } = useFinalRanking(id);
  const { scenarioWeights } = useScenarioWeights(id);
  const { file: fileFromHook, fileLoading, fileError, refetch } = useReportFilesLasted(id);

  const [file, setFile] = useState(fileFromHook);
  const [localFile, setLocalFile] = useState(fileFromHook);
  const [showEditor, setShowEditor] = useState(false);
  const [localReportStatus, setLocalReportStatus] = useState(report?.status);
 // console.log('Local Report Status:', localReportStatus);

  const [initialReportStatus, setInitialReportStatus] = useState<string | undefined>(report?.status);

  // Aggiorna lo stato locale quando il report cambia
  useEffect(() => {
    setLocalReportStatus(report?.status);
  }, [report?.status]);

  useEffect(() => {
    setLocalFile(fileFromHook);
    setFile(fileFromHook);
  }, [fileFromHook]);

  // ---------- HANDLERS ----------

  const handleGenerateReport = async () => {
    const result = await Swal.fire({
      title: "Generare il report?",
      text: "Questa operazione potrebbe richiedere alcuni minuti.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sì, genera",
      cancelButtonText: "Annulla",
      confirmButtonColor: "#374151",
      cancelButtonColor: "#9CA3AF",
    });

    if (!result.isConfirmed) return;

    Swal.fire({
      title: "Generazione in corso...",
      text: "Attendere il completamento.",
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      const newFile = await createReportFile(id!);
      if (newFile) {
        setLocalFile(newFile);
        setFile(newFile);
      }

      Swal.close();
      setInitialReportStatus(localReportStatus);
      setShowEditor(true);

      Swal.fire({
        title: "Completato!",
        text: "Il report è stato generato correttamente.",
        icon: "success",
      });

      refetch();
    } catch {
      Swal.fire({
        title: "Errore",
        text: "Impossibile generare il report.",
        icon: "error",
      });
    }
  };

  const handleViewReport = () => {
    if (!file) {
      Swal.fire({
        title: "File non disponibile",
        text: "Non è stato trovato alcun file da visualizzare.",
        icon: "warning",
      });
      return;
    }
    setInitialReportStatus(localReportStatus);
    setShowEditor(true);
  };

  const handleCloseEditor = async () => {
    setShowEditor(false);
    await refetch();
    const updatedReport = await fetchReportById(id!);
    setLocalReportStatus(updatedReport.status);
    setLocalFile(await refetch());
  };


  const handleExportReport = async () => {
    if (!id) return Swal.fire("⚠️", "ID report non definito.", "error");

    try {
      Swal.fire({
        title: "Esportazione in corso...",
        text: "Generazione del file Excel, attendere qualche secondo.",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      const blob = await fetchExportXlsxById(id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
      a.download = `report-ranking-${timestamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      Swal.close();
      Swal.fire({
        icon: "success",
        title: "Download completato!",
        text: "Il file Excel è stato scaricato correttamente.",
        confirmButtonColor: "#374151",
      });
    } catch (error: any) {
      Swal.close();
      Swal.fire("⚠️", error.message || "Errore durante il download del file Excel.", "error");
    }
  };

  const handleReopenReport = async () => {
    if (!id) return Swal.fire("⚠️", "ID report non definito.", "error");

    const result = await Swal.fire({
      title: "Riaprire il report?",
      text: "Sei sicuro di voler riaprire questo report?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sì, riapri",
      cancelButtonText: "Annulla",
      reverseButtons: true,
      confirmButtonColor: "#374151",
      cancelButtonColor: "#9CA3AF",
    });

    if (!result.isConfirmed) return;

    Swal.fire({
      title: "Riapertura in corso...",
      text: "Attendere il completamento.",
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      await fetchReopenById(id);
      Swal.close();
      setLocalReportStatus("drafted");

      Swal.fire({
        icon: "success",
        title: "Report riaperto!",
        text: "Il report è stato riaperto correttamente.",
        confirmButtonColor: "#374151",
      }).then(() => {
        navigate(routesMap.VISUALIZZA_REPORT(name!), { state: { id } });
      });

      refetch();
    } catch (error: any) {
      Swal.close();
      Swal.fire("⚠️", error.message || "Errore durante la riapertura del report.", "error");
    }
  };

  const handleCompleteReport = async () => {
    if (!id) return Swal.fire("⚠️", "ID report non definito.", "error");

    const result = await Swal.fire({
      title: "Completare il report?",
      text: "Sei sicuro di voler completare questo report? Non potrà più essere modificato.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sì, completa",
      cancelButtonText: "Annulla",
      confirmButtonColor: "#374151",
      cancelButtonColor: "#9CA3AF",
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    Swal.fire({
      title: "Completamento in corso...",
      text: "Attendere il completamento.",
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      await fetchCompleteById(id);
      Swal.close();

      // ✅ Aggiorna solo lo stato locale
      setLocalReportStatus("completed");

      Swal.fire({
        icon: "success",
        title: "Report completato!",
        text: "Il report è stato completato correttamente.",
        confirmButtonColor: "#374151",
      });

      // ✅ Aggiorna il file locale
      refetch();
    } catch (error: any) {
      Swal.close();
      Swal.fire("⚠️", error.message || "Errore durante il completamento del report.", "error");
    }
  };

  if (loadingReport) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="w-20 h-20 border-[6px] border-gray-300 border-t-textPrimary rounded-full animate-spin" />
      </div>
    );
  }

  if (errorReport) {
    return <div className="p-6 text-red-600">{errorReport}</div>;
  }

  return (
    <div className="flex flex-col bg-gray-50">
      <BannerCalcoloRanking name={name} title="Ranking" reportId={id} />

      <div className="px-8 lg:px-16 py-8">
        <div className="flex justify-end gap-3 mb-6">
        {(fileLoading || (!file && !fileError)) ? (
          <button
            disabled
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-[#1B365D] text-white hover:bg-[#142a4a] transition-colors opacity-60 cursor-not-allowed"
          >
            Caricamento...
          </button>
        ) : fileError || !file ? (
          <ReportButton onClick={handleGenerateReport} label="Genera Report" />

        ) : (
          <ReportButton onClick={handleViewReport} label="Visualizza Report" />

        )}
          <ReportButton onClick={handleExportReport} label="Esporta Tabelle" />


          {!localReportStatus ? (
            <div className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-[#1B365D] text-white opacity-50 cursor-not-allowed">
              Caricamento...
            </div>
          ) : (
            <>
              {/* Bottone Completa Report: visibile solo se drafted */}
              {localReportStatus === "drafted" && (
                <button
                  onClick={handleCompleteReport}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-[#1B365D] text-white hover:bg-[#142a4a] transition-colors"
                >
                  Completa Report
                </button>
              )}

              {/* Bottone Modifica Driver: visibile se drafted, completed o approved */}
              {["drafted", "completed", "approved"].includes(localReportStatus) && (
                <button
                  onClick={handleReopenReport}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-[#1B365D] text-white hover:bg-[#142a4a] transition-colors"
                >
                  Modifica Driver
                </button>
              )}
            </>
          )}
        </div>

        <ReportEditorModal
          key={file?.id || "report-editor"}
          isOpen={showEditor}
          onClose={handleCloseEditor}
          reportId={id!}
          jsonContent={file?.json_content}
          reportStatus={localReportStatus}
        />

        <ContainerCalcoloRanking
          driverScores={driverScores}
          scenarioScores={scenarioScores}
          finalRanking={finalRanking}
          scenarioWeights={scenarioWeights}
        />
      </div>
    </div>
  );
};

export default RankingReportPage;
